import React, { useState, useRef } from 'react';
import { Box, Container, Button as ChakraButton, CloseButton } from '@chakra-ui/react';
import { Slide } from '@chakra-ui/transition';
import { AlertRoot, AlertIndicator, AlertTitle, AlertDescription } from '@chakra-ui/react';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody } from '@chakra-ui/modal';
import PlayerSetup from './components/PlayerSetup';
import Scoreboard from './components/Scoreboard';
import ScoreInput from './components/ScoreInput';
import WinnerModal from './components/WinnerModal';
import { Player } from './types';

const WINNING_SCORE = 10000;

const App: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [winner, setWinner] = useState<Player | null>(null);
  const [lastPlayerIndex, setLastPlayerIndex] = useState<number | null>(null);
  const [showScoreError, setShowScoreError] = useState(false);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cancelRef = useRef(null);

  const handleStart = (names: string[]) => {
    setPlayers(names.map((name) => ({ name, scores: [], total: 0 })));
    setCurrentPlayerIndex(0);
    setGameStarted(true);
    setWinner(null);
    setLastPlayerIndex(null);
  };

  const handleScoreSubmit = (score: number) => {
    setPlayers((prevPlayers) => {
      const updated = [...prevPlayers];
      const player = { ...updated[currentPlayerIndex] };
      player.scores = [...player.scores, score];
      player.total += score;
      // Check for over WINNING_SCORE
      if (player.total > WINNING_SCORE) {
        setShowScoreError(true);
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = setTimeout(() => setShowScoreError(false), 3000);
        return prevPlayers; // Don't update state
      }
      updated[currentPlayerIndex] = player;
      console.log({player}); 
      console.log({updated});
      // Winner and turn logic must be handled after state update
      if (player.total === WINNING_SCORE) {
        setWinner(player);
        // Do not advance turn if there's a winner
      } else {
        setCurrentPlayerIndex((prev) => (prev + 1) % updated.length);
      }
      setLastPlayerIndex(currentPlayerIndex);
      return updated;
    });
  };

  const handleUndoLast = () => {
    if (lastPlayerIndex === null) return;
    setPlayers((prev) => {
      const updated = [...prev];
      const player = { ...updated[lastPlayerIndex] };
      if (player.scores.length > 0) {
        const lastScore = player.scores.pop()!;
        player.total -= lastScore;
        updated[lastPlayerIndex] = player;
      }
      return updated;
    });
    setCurrentPlayerIndex(lastPlayerIndex);
    setLastPlayerIndex(null);
  };

  const handleRestart = () => {
    setPlayers([]);
    setCurrentPlayerIndex(0);
    setGameStarted(false);
    setWinner(null);
    setLastPlayerIndex(null);
  };

  const canUndo =
    lastPlayerIndex !== null &&
    players[lastPlayerIndex] &&
    players[lastPlayerIndex].scores.length > 0;

  return (
    <Box minH="100vh" bgGradient="linear(to-br, teal.50, blue.100)">
      <Container maxW="lg" py={8}>
        <Slide direction="top" in={showScoreError} style={{ zIndex: 999 }}>
          <AlertRoot status="error" style={{ borderRadius: '8px', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
            <AlertIndicator />
            <Box flex="1">
              <AlertTitle>Score Too High!</AlertTitle>
              <AlertDescription>
                The total score must be exactly {WINNING_SCORE} to win. Please enter a score that brings the total to exactly {WINNING_SCORE}.
              </AlertDescription>
            </Box>
            <CloseButton position="absolute" right="8px" top="8px" onClick={() => setShowScoreError(false)} />
          </AlertRoot>
        </Slide>
        {!gameStarted && <PlayerSetup onStart={handleStart} />}
        {gameStarted && !winner && players.length > 0 && players[currentPlayerIndex] && (
          <>
            <Scoreboard players={players} currentPlayerIndex={currentPlayerIndex} />
            <ScoreInput
              playerName={players[currentPlayerIndex].name}
              onSubmit={handleScoreSubmit}
              onUndoLast={handleUndoLast}
              canUndo={canUndo}
            />
          </>
        )}
        {winner && (
          <WinnerModal winnerName={winner.name} onRestart={() => {
            console.log('restart');
            handleRestart()
          }} />
        )}
      </Container>
    </Box>
  );
};

export default App; 